import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, ArrowRight, Flame } from 'lucide-react';
import OccupationLink from '@/components/shared/OccupationLink';
import { normalizeSocCode } from '@/lib/occupationUtils';
import { supabase } from '@/integrations/supabase/client';

interface RankedOccupation {
  rank: number;
  soc_code: string;
  occ_title: string;
  median_salary: number;
  slug: string;
}

interface HomepageRankingsWidgetProps {
  selectedOccupation: { code: string; title: string } | null;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);

const HomepageRankingsWidget = ({ selectedOccupation }: HomepageRankingsWidgetProps) => {
  // Always fetch the full ranked list (cached for 1 hour)
  const { data, isLoading } = useQuery({
    queryKey: ['homepage-rankings-all', 'v2'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: { search: '*', limit: 10000, offset: 0 },
      });
      if (error) throw error;
      return data?.occupations || [];
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 2,
  });

  // Sort by median salary descending and assign ranks
  const rankedList: RankedOccupation[] = useMemo(() => {
    if (!data) return [];
    return [...data]
      .filter((o: { median_salary?: number }) => o.median_salary && o.median_salary > 0)
      .sort((a: { median_salary: number }, b: { median_salary: number }) => b.median_salary - a.median_salary)
      .map((o: { soc_code: string; occ_title: string; median_salary: number; slug: string }, i: number) => ({
        rank: i + 1,
        soc_code: o.soc_code,
        occ_title: o.occ_title,
        median_salary: o.median_salary,
        slug: o.slug,
      }));
  }, [data]);

  // Find the selected occupation's rank
  const selectedIndex = useMemo(() => {
    if (!selectedOccupation || rankedList.length === 0) return -1;
    const selectedCode = normalizeSocCode(selectedOccupation.code);
    return rankedList.findIndex((o) => normalizeSocCode(o.soc_code) === selectedCode);
  }, [selectedOccupation, rankedList]);

  // Determine which rows to display
  const displayRows: RankedOccupation[] = useMemo(() => {
    if (rankedList.length === 0) return [];

    // Contextual state: center on the selected occupation
    if (selectedIndex >= 0) {
      const start = Math.max(0, selectedIndex - 5);
      const end = Math.min(rankedList.length, selectedIndex + 6);
      return rankedList.slice(start, end);
    }

    // Default state: top 10
    return rankedList.slice(0, 10);
  }, [rankedList, selectedIndex]);

  const isContextual = selectedIndex >= 0;
  const totalOccupations = rankedList.length;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-7 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (displayRows.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Trophy className="h-5 w-5 text-amber-500 shrink-0" />
            {isContextual ? 'Where You Rank' : 'Highest Paying Jobs'}
          </CardTitle>
          {isContextual && (
            <Badge variant="secondary" className="text-sm font-medium">
              #{selectedIndex + 1} of {totalOccupations}
            </Badge>
          )}
        </div>
        {isContextual ? (
          <p className="text-sm text-muted-foreground mt-1">
            {selectedOccupation?.title} ranks <strong>#{selectedIndex + 1}</strong> out of {totalOccupations} occupations by median salary.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground mt-1">
            Top careers by median salary from official BLS OEWS data.
          </p>
        )}
      </CardHeader>
      <CardContent>
        {/* Rankings Table */}
        <div className="space-y-1">
          {displayRows.map((occ) => {
            const isHighlighted = isContextual
              && normalizeSocCode(occ.soc_code) === normalizeSocCode(selectedOccupation?.code || '');
            return (
              <div
                key={occ.soc_code}
                className={`flex items-center gap-2 px-0 py-2.5 sm:gap-3 sm:px-1 rounded-md transition-colors ${
                  isHighlighted
                    ? 'bg-wage-accent/10 ring-1 ring-wage-accent/30 font-medium'
                    : 'hover:bg-muted/50'
                }`}
              >
                {/* Rank */}
                <span className={`w-9 sm:w-12 text-right text-sm font-mono shrink-0 ${
                  occ.rank <= 3 ? 'text-amber-600 font-bold' : 'text-muted-foreground'
                }`}>
                  #{occ.rank}
                </span>

                {/* Title */}
                <div className="flex-1 min-w-0">
                  <OccupationLink
                    occupation={{ occ_title: occ.occ_title, soc_code: occ.soc_code, slug: occ.slug }}
                    location="united-states"
                    className={`text-sm truncate block ${isHighlighted ? 'font-semibold' : ''}`}
                  >
                    {occ.occ_title}
                  </OccupationLink>
                </div>

                {/* Salary */}
                <span className={`text-sm font-medium whitespace-nowrap ${
                  isHighlighted ? 'text-wage-primary' : ''
                }`}>
                  {formatCurrency(occ.median_salary)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Footer CTAs */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-5 pt-4 border-t">
          <Link
            to="/rankings/"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            See all highest paying jobs
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/in-demand-careers/"
            className="inline-flex items-center gap-1 text-sm font-medium text-orange-600 dark:text-orange-400 hover:underline"
          >
            <Flame className="h-3.5 w-3.5" />
            In-demand careers with shortages
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default HomepageRankingsWidget;
